import { request } from './client';

/**
 * Tầng gọi API của cơ hội bán hàng.
 */

/** Đường dẫn gốc của cơ hội bán hàng. */
const opportunityRoute = '/api/v1/opportunities';

/**
 * Giai đoạn của một cơ hội.
 *
 * Giá trị phải khớp với <c>SalesOpportunityStage</c> ở máy chủ.
 */
export const SalesOpportunityStage = {
  /** Khảo sát — đang tìm hiểu nhu cầu. */
  Survey: 1,

  /** Đang báo giá. */
  Quoting: 2,

  /** Đàm phán. */
  Negotiating: 3,

  /** Chờ khách phát hành đơn đặt hàng. */
  AwaitingPurchaseOrder: 4,

  /** Thắng. */
  Won: 5,

  /** Thua — khách chọn đơn vị khác. */
  Lost: 6,

  /** Khách dừng — không ai làm cả. */
  Abandoned: 7,
} as const;

/** Giá trị của giai đoạn. */
export type SalesOpportunityStageValue =
  (typeof SalesOpportunityStage)[keyof typeof SalesOpportunityStage];

/** Tên tiếng Việt của từng giai đoạn. */
export const salesOpportunityStageNames: Readonly<
  Record<SalesOpportunityStageValue, string>
> = {
  [SalesOpportunityStage.Survey]: 'Khảo sát',
  [SalesOpportunityStage.Quoting]: 'Đang báo giá',
  [SalesOpportunityStage.Negotiating]: 'Đàm phán',
  [SalesOpportunityStage.AwaitingPurchaseOrder]: 'Chờ PO',
  [SalesOpportunityStage.Won]: 'Thắng',
  [SalesOpportunityStage.Lost]: 'Thua',
  [SalesOpportunityStage.Abandoned]: 'Khách dừng',
};

/**
 * Ba giai đoạn kết thúc.
 *
 * Chuyển sang một trong ba giai đoạn này thì máy chủ bắt buộc ghi lý do, nên giao diện phải biết
 * để hiện ô lý do trước khi người dùng bấm lưu và nhận về một lỗi khó hiểu.
 */
export const closingStages: readonly SalesOpportunityStageValue[] = [
  SalesOpportunityStage.Won,
  SalesOpportunityStage.Lost,
  SalesOpportunityStage.Abandoned,
];

/** Một cơ hội bán hàng. */
export interface SalesOpportunity {
  readonly id: string;
  readonly code: string;
  readonly customerId: string;
  readonly customerName: string | null;
  readonly name: string;
  readonly description: string | null;
  readonly stage: SalesOpportunityStageValue;
  readonly estimatedValue: number | null;
  readonly currencyCode: string | null;
  readonly winProbabilityPercent: number | null;

  /** Giá trị đã nhân xác suất. Rỗng khi cơ hội đã đóng. */
  readonly weightedValue: number | null;

  readonly expectedCloseDate: string | null;
  readonly ownerUserId: string | null;
  readonly closedAt: string | null;
  readonly outcomeReason: string | null;
  readonly competitor: string | null;
  readonly projectId: string | null;
  readonly isClosed: boolean;
  readonly isOverdue: boolean;

  /** Số ngày đã theo đuổi, tính tới lúc đóng hoặc tới hôm nay. */
  readonly daysOpen: number;
}

/** Một dòng của bản tổng hợp phễu, gom theo giai đoạn. */
export interface SalesPipelineStageSummary {
  readonly stage: SalesOpportunityStageValue;
  readonly count: number;
  readonly totalValue: number;
  readonly weightedValue: number;
}

/** Bản tổng hợp phễu bán hàng. */
export interface SalesPipelineSummary {
  readonly opportunities: readonly SalesOpportunity[];
  readonly stages: readonly SalesPipelineStageSummary[];
  readonly openCount: number;
  readonly overdueCount: number;
  readonly openValue: number;
  readonly openWeightedValue: number;
  readonly wonCount: number;
  readonly lostCount: number;
  readonly abandonedCount: number;

  /** Tỷ lệ thắng trên tổng số cơ hội đã có kết quả. Không tính cơ hội khách dừng. */
  readonly winRatePercent: number | null;
}

/** Điều kiện lọc danh sách cơ hội. */
export interface SalesOpportunityFilter {
  readonly stage?: SalesOpportunityStageValue | undefined;
  readonly customerId?: string | undefined;
  readonly ownerUserId?: string | undefined;
  readonly onlyOpen?: boolean | undefined;
  readonly searchTerm?: string | undefined;
}

/** Nội dung khai hoặc sửa một cơ hội. */
export interface SaveSalesOpportunityPayload {
  readonly customerId: string;
  readonly name: string;
  readonly description: string | null;
  readonly estimatedValue: number | null;
  readonly currencyCode: string | null;
  readonly winProbabilityPercent: number | null;
  readonly expectedCloseDate: string | null;
  readonly ownerUserId: string | null;
}

/** Nội dung chuyển giai đoạn. */
export interface ChangeSalesOpportunityStagePayload {
  readonly stage: SalesOpportunityStageValue;
  readonly outcomeReason: string | null;
  readonly competitor: string | null;
}

/** Đọc danh sách cơ hội kèm bản tổng hợp phễu. */
export async function listOpportunities(
  filter: SalesOpportunityFilter = {},
): Promise<SalesPipelineSummary> {
  const parameters = new URLSearchParams();

  if (filter.stage !== undefined) {
    parameters.set('stage', String(filter.stage));
  }

  if (filter.customerId !== undefined) {
    parameters.set('customerId', filter.customerId);
  }

  if (filter.ownerUserId !== undefined) {
    parameters.set('ownerUserId', filter.ownerUserId);
  }

  if (filter.onlyOpen === true) {
    parameters.set('onlyOpen', 'true');
  }

  if (filter.searchTerm !== undefined && filter.searchTerm.trim() !== '') {
    parameters.set('searchTerm', filter.searchTerm.trim());
  }

  const query = parameters.size === 0 ? '' : `?${parameters.toString()}`;

  return await request<SalesPipelineSummary>(`${opportunityRoute}${query}`);
}

/** Khai một cơ hội mới. Mã do máy chủ tự sinh. */
export async function createOpportunity(
  payload: SaveSalesOpportunityPayload,
): Promise<SalesOpportunity> {
  return await request<SalesOpportunity>(opportunityRoute, { method: 'POST', body: payload });
}

/** Sửa thông tin một cơ hội. Không đổi giai đoạn. */
export async function updateOpportunity(
  id: string,
  payload: SaveSalesOpportunityPayload,
): Promise<SalesOpportunity> {
  return await request<SalesOpportunity>(`${opportunityRoute}/${id}`, {
    method: 'PUT',
    body: payload,
  });
}

/** Chuyển giai đoạn một cơ hội. */
export async function changeOpportunityStage(
  id: string,
  payload: ChangeSalesOpportunityStagePayload,
): Promise<SalesOpportunity> {
  return await request<SalesOpportunity>(`${opportunityRoute}/${id}/stage`, {
    method: 'PUT',
    body: payload,
  });
}

/** Ghi nhận dự án đã mở từ một cơ hội đã thắng. */
export async function linkOpportunityProject(
  id: string,
  projectId: string,
): Promise<SalesOpportunity> {
  return await request<SalesOpportunity>(`${opportunityRoute}/${id}/project`, {
    method: 'PUT',
    body: { projectId },
  });
}
